'use client';

import { Box, OutlinedInput, Typography } from '@mui/material';
import { usePageAction } from '@repo/hooks/usePageAction';
import { useShallow } from 'zustand/shallow';
import { useHYSACalculatorStore } from './calculatorStore';
import { formatToDisplayValue } from './utils';

export function MonthlyDepositForm() {
  const fireHysaCalculatorInteractedPageAction = usePageAction({
    actionName: 'hysa_calculator_interacted',
    actionSection: 'banking',
    actionType: 'select',
  });
  const {
    annualPercentageYield,
    initialDeposit,
    monthlyDeposit,
    monthlyDepositError,
    setField,
    timePeriod,
  } = useHYSACalculatorStore(
    useShallow(state => ({
      annualPercentageYield: state.formFields.annualPercentageYield,
      initialDeposit: state.formFields.initialDeposit.includes(',')
        ? state.formFields.initialDeposit.replace(/,/g, '')
        : formatToDisplayValue(state.formFields.initialDeposit),
      monthlyDeposit: state.formFields.monthlyDeposit.includes(',')
        ? state.formFields.monthlyDeposit
        : formatToDisplayValue(state.formFields.monthlyDeposit),
      monthlyDepositError: state.errors.monthlyDeposit,
      setField: state.setField,
      timePeriod: state.formFields.timePeriod,
    })),
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.125rem',
      }}
    >
      <Typography component="label" variant="textSmallBold" htmlFor="monthly-deposit">
        Monthly deposit
      </Typography>
      <OutlinedInput
        id="monthly-deposit"
        onChange={e => {
          const target = e.target.value.replace(/,/g, '');

          if (!/^\d{0,7}(\.\d{0,2})?$/g.test(target)) {
            return;
          }

          fireHysaCalculatorInteractedPageAction({
            additional_attributes: {
              annual_percentage_yield_value_input: annualPercentageYield,
              initial_deposit_value_input: initialDeposit,
              monthly_deposit_value_input: target,
              time_period_value_input: timePeriod,
            },
          });
          setField('monthlyDeposit', target);
        }}
        slotProps={{
          input: {
            inputMode: 'decimal',
            sx: {
              padding: 0,
            },
          },
        }}
        startAdornment={
          <Typography variant="text" sx={{ color: 'neutral.main', fontWeight: 325 }}>
            $
          </Typography>
        }
        sx={{
          alignItems: 'flex-start',
          borderRadius: '0.5rem',
          display: 'flex',
          gap: '.5rem',
          paddingX: '1rem',
          paddingY: '0.5rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: monthlyDepositError ? 'reds.main' : 'blues.light',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: monthlyDepositError ? 'reds.main' : 'blues.light',
          },
        }}
        value={monthlyDeposit}
      />
      {monthlyDepositError && (
        <Typography variant="textExtraSmall" sx={{ color: 'reds.main' }}>
          {monthlyDepositError}
        </Typography>
      )}
    </Box>
  );
}
